import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow
} from '@angular/material/table';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConfirmBoxComponent } from '../../../components/confirm-box/confirm-box.component';
import { DynamicDialogService } from '../../../components/dynamic-dialog/dynamic-dialog-service';
import { MessagingService } from '../../../components/messaging/messaging.service';
import { mediumModalConfig, wideModalConfig } from '../../../consts/modal-config.const';
import { ResponseEnumEnum } from '../../../enums/responseEnum.enum';
import { ModalConfigsHelper } from '../../../helpers/modal-configs.helper';
import { FactoringCompany } from '../../../model/factoring-company.model';
import { SysAdminService } from '../../../services/sys-admin.service';
import { ToolbarService } from '../../../services/toolbar.service';
import { AddFactoringCompanyComponent } from '../add-factoring-company/add-factoring-company.component';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'app-factoring-companies-section',
  templateUrl: './factoring-companies-section.component.html',
  styleUrls: ['./factoring-companies-section.component.scss'],
  imports: [
    MatButton,
    MatDivider,
    MatIconButton,
    MatIcon,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow
  ]
})
export class FactoringCompaniesSectionComponent implements OnChanges, OnDestroy {
  @Input() factoringCompaniesList: FactoringCompany[] = [];
  @Output() updateFactoringCompanies: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('factoringCompaniesSort', { static: true }) factoringCompaniesSort: MatSort;
  factoringCompaniesDataSource: MatTableDataSource<FactoringCompany>;
  activeFactoringCompaniesColumn = 'companyName';
  factoringCompaniesColumnHeaders = ['companyName', 'abn', 'email', 'phone', 'addressLine1', 'deleteCompany'];
  factoringCompaniesExpanded = false;
  private unsubscribe = new Subject<void>();

  #dynamicDialogService = inject(DynamicDialogService);
  constructor(
    private message: MessagingService,
    public toolbarService: ToolbarService,
    private sysAdminService: SysAdminService,
    private confirmBox: MatDialog,
    private modalConfigsHelper: ModalConfigsHelper
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    this.factoringCompaniesDataSource = new MatTableDataSource(changes.factoringCompaniesList.currentValue);
    this.factoringCompaniesDataSource.sortingDataAccessor = this.sortingDataAccessor;
    this.factoringCompaniesDataSource.sort = this.factoringCompaniesSort;
  }

  addEditFactoringCompany(factoringCompany?: FactoringCompany) {
    const dialogConfig = {
      ...wideModalConfig,
      data: {
        title: 'Factoring Company Information',
        action: 'SAVE',
        displayClose: true,
        component: AddFactoringCompanyComponent,
        factoringCompany: factoringCompany ?? null,
        existingAbns: this.factoringCompaniesList.map((fc) => fc.abn)
      }
    };
    const dialogRef = this.#dynamicDialogService.openDialog(dialogConfig);
    dialogRef.afterClosed().subscribe((result) => {
      if (result === ResponseEnumEnum.SUCCESS) {
        this.message.confirm(`Factoring Company ${factoringCompany ? 'Edited' : 'Added'}`);
        this.updateFactoringCompanies.emit();
      }
    });
  }

  confirmDeleteFactoringCompany(id: number) {
    const data = {
      title: 'Delete Factoring Company',
      alertMessage: 'Factoring Company will be deleted.',
      confirmButton: 'DELETE'
    };
    const dialogRef = this.confirmBox.open(
      ConfirmBoxComponent,
      this.modalConfigsHelper.buildMediumModalConfig(data, mediumModalConfig)
    );
    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'DELETE') {
        this.deleteFactoringCompany(id);
      }
    });
  }

  deleteFactoringCompany(id: number) {
    this.toolbarService.setCloudState('SAVING');
    this.sysAdminService
      .deleteFactoringCompany(id)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: () => this.toolbarService.setCloudState('RESTING'),
        error: () => {
          this.message.error('Factoring Company could not be deleted.');
          this.toolbarService.setCloudState('RESTING');
        },
        complete: () => {
          this.message.confirm('Factoring Company Deleted');
          this.updateFactoringCompanies.emit();
        }
      });
  }

  sortingDataAccessor(item, property) {
    if (property.includes('.')) {
      if (property === 'mainContact.firstName') {
        return (item.mainContact.firstName + item.mainContact.lastName).toLowerCase();
      }
      return property
        .split('.')
        .reduce((object, key) => object[key], item)
        .toLowerCase();
    }
    return item[property] ? item[property].toString().toLowerCase() : '';
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
